import type { SupportedChainId } from '@eulerxyz/core-sdk'
import { contractBook } from '@eulerxyz/core-sdk'
import type { BatchResultStatus, ContractFunctionConfig, ContractFunctionResult, EVCCall } from '@eulerxyz/evc-sdk'
import { getAddress } from 'viem'
import { BaseTransactionFactory } from './BaseTransactionFactory'
import { EVCTransactionFactory } from './EVCTransactionFactory'
import { EulerSwapTransactionFactory } from './EulerSwapTransactionFactory'
import { FeeFlowTransactionFactory } from './FeeFlowTransactionFactory'
import { KeyringTransactionFactory } from './KeyringTransactionFactory'
import { Permit2TransactionFactory } from './Permit2TransactionFactory'
import { RewardStreamsTransactionFactory } from './RewardStreamsTransactionFactory'
import { SwapTransactionFactory } from './SwapTransactionFactory'
import { SwapVerifierTransactionFactory } from './SwapVerifierTransactionFactory'
import { TosTransactionFactory } from './TosTransactionFactory'
import { VaultTransactionFactory } from './VaultTransactionFactory'
import { WETHTransactionFactory } from './WETHTransactionFactory'
import {
  type TransactionItemUnion,
  type TransactionItems,
  isCheckPositionStatusTransactionItem,
  isCompoundTransaction,
  isDisableTransactionItem,
  isEVCTransactionItem,
  isEulerSwapTransactionItem,
  isFeeFlowTransactionItem,
  isKeyringTransactionItem,
  isMerklTransaction,
  isPermitTransactionItem,
  isRewardStreamsTransaction,
  isSwapTransaction,
  isSwapVerifierTransactionItem,
  isTosTransactionItem,
  isUnlockREulTransaction,
  isUnwrapTransactionItem,
  isVaultTransaction,
  isWETHTransactionItem,
  isWrapTransactionItem,
  isWrappingOrUnwrapping,
} from './types'

export class GeneralTransactionFactoryUnifier extends BaseTransactionFactory {
  swapTransactionFactory: SwapTransactionFactory
  vaultTransactionFactory: VaultTransactionFactory
  wethTransactionFactory: WETHTransactionFactory
  permit2TransactionFactory: Permit2TransactionFactory
  evcTransactionFactory: EVCTransactionFactory
  rewardStreamsTransactionFactory: RewardStreamsTransactionFactory
  swapVerifierTransactionFactory: SwapVerifierTransactionFactory
  tosTransactionFactory: TosTransactionFactory
  feeFlowTransactionFactory: FeeFlowTransactionFactory
  eulerSwapTransactionFactory: EulerSwapTransactionFactory
  keyringTransactionFactory: KeyringTransactionFactory

  constructor(chainId: SupportedChainId, _contractBook: typeof contractBook = contractBook) {
    super(chainId, _contractBook)
    this.swapTransactionFactory = new SwapTransactionFactory(chainId, _contractBook)

    this.vaultTransactionFactory = new VaultTransactionFactory(chainId, _contractBook)

    this.permit2TransactionFactory = new Permit2TransactionFactory(chainId, _contractBook)

    this.evcTransactionFactory = new EVCTransactionFactory(chainId, _contractBook)

    this.rewardStreamsTransactionFactory = new RewardStreamsTransactionFactory(chainId, _contractBook)

    this.swapVerifierTransactionFactory = new SwapVerifierTransactionFactory(chainId, _contractBook)

    this.tosTransactionFactory = new TosTransactionFactory(chainId, contractBook)

    this.feeFlowTransactionFactory = new FeeFlowTransactionFactory(chainId, contractBook)

    this.wethTransactionFactory = new WETHTransactionFactory(chainId, contractBook)

    this.eulerSwapTransactionFactory = new EulerSwapTransactionFactory(chainId, contractBook)

    this.keyringTransactionFactory = new KeyringTransactionFactory(chainId, contractBook)
  }

  typeGuardConvertMultiple(_transactions: TransactionItems): (ContractFunctionConfig | EVCCall)[] {
    let contractFunctionConfigs: (ContractFunctionConfig | EVCCall)[] = []
    const checkPositionStatusTransactions: ContractFunctionConfig[] = []
    for (const tx of _transactions) {
      if (isWrapTransactionItem(tx)) {
        const calls = this.wethTransactionFactory.typeGuardConvertMultiple([tx])
        contractFunctionConfigs.push(...calls)
      }

      if (isKeyringTransactionItem(tx)) {
        const calls = this.keyringTransactionFactory.typeGuardConvertMultiple([tx])
        contractFunctionConfigs.push(...calls)
      }

      if ('transactions' in tx && tx.transactions) {
        const subCalls = this.typeGuardConvertMultiple(
          tx.transactions.filter((tsx) => !isDisableTransactionItem(tsx) && !isCheckPositionStatusTransactionItem(tsx)),
        )

        contractFunctionConfigs.push(...subCalls)
      }

      if (
        !isCompoundTransaction(tx) &&
        !isWETHTransactionItem(tx) &&
        !isWrappingOrUnwrapping(tx) &&
        !isKeyringTransactionItem(tx) &&
        !isCheckPositionStatusTransactionItem(tx)
      ) {
        const call = this.typeGuardConvertSingle(tx)
        // Handle array results (e.g., from SkimIn which produces multiple calls)
        if (Array.isArray(call)) {
          contractFunctionConfigs.push(...call)
        } else {
          contractFunctionConfigs.push(call)
        }
      }

      if ('transactions' in tx && tx.transactions) {
        const subCalls = this.typeGuardConvertMultiple(tx.transactions.filter(isDisableTransactionItem))

        contractFunctionConfigs.push(...subCalls)
      }
      if (isUnwrapTransactionItem(tx)) {
        const calls = this.wethTransactionFactory.typeGuardConvertMultiple([tx])
        contractFunctionConfigs.push(...calls)
      }

      if ('transactions' in tx && tx.transactions) {
        const subCalls = this.typeGuardConvertMultiple(
          tx.transactions.filter((tsx) => isCheckPositionStatusTransactionItem(tsx)),
        )

        checkPositionStatusTransactions.push(...(subCalls as ContractFunctionConfig[]))
      }
    }

    const addresses = checkPositionStatusTransactions.map((tx) => getAddress(tx?.address))
    const isSameVault = contractFunctionConfigs.every(
      (tx) => 'address' in tx && addresses.includes(getAddress(tx?.address)),
    )
    if (!isSameVault) {
      // If has any kind of transaction with the same vault we don't add the check position to avoid issues.
      contractFunctionConfigs = [...checkPositionStatusTransactions, ...contractFunctionConfigs]
    }

    return contractFunctionConfigs
  }

  typeGuardConvertMultipleWithPositions(_transactions: TransactionItems): {
    configs: (ContractFunctionConfig | EVCCall)[]
    positions: number[][]
  } {
    const contractFunctionConfigs: (ContractFunctionConfig | EVCCall)[] = []
    const positions: number[][] = []

    const traverseEvcCalls = (evcCalls: EVCCall[], path: number[] = []) => {
      for (let i = 0; i < evcCalls.length; i++) {
        const call = evcCalls[i]
        const currentPath = [...path, i]
        // @ts-ignore - Type might be undefined
        contractFunctionConfigs.push(call)
        positions.push(currentPath)
      }
    }

    const traverse = (transactions: TransactionItems, path: number[] = []) => {
      for (let i = 0; i < transactions.length; i++) {
        const tx = transactions[i]
        const currentPath = [...path, i]
        if (isWrapTransactionItem(tx)) {
          const calls = this.wethTransactionFactory.typeGuardConvertMultiple([tx])
          traverseEvcCalls(calls, currentPath)
        }

        if (isKeyringTransactionItem(tx)) {
          const calls = this.keyringTransactionFactory.typeGuardConvertMultiple([tx])
          contractFunctionConfigs.push(...calls)
        }

        // @ts-ignore - tx might be undefined
        if ('transactions' in tx && tx.transactions) {
          traverse(
            tx.transactions.filter((tsx) => !isDisableTransactionItem(tsx)),
            currentPath,
          )
        }
        if (
          !isCompoundTransaction(tx) &&
          !isWETHTransactionItem(tx) &&
          !isWrappingOrUnwrapping(tx) &&
          !isKeyringTransactionItem(tx)
        ) {
          // @ts-ignore - Type narrowing issue
          const call = this.typeGuardConvertSingle(tx)
          // @ts-ignore - Type might be array
          contractFunctionConfigs.push(call)
          positions.push(currentPath)
        }

        // @ts-ignore - tx might be undefined
        if ('transactions' in tx && tx.transactions) {
          traverse(tx.transactions.filter(isDisableTransactionItem), currentPath)
        }

        if (isUnwrapTransactionItem(tx)) {
          const calls = this.wethTransactionFactory.typeGuardConvertMultiple([tx])
          traverseEvcCalls(calls, currentPath)
        }
      }
    }

    traverse(_transactions)
    return { configs: contractFunctionConfigs, positions }
  }

  mapResultsBack<results extends BatchResultStatus<ContractFunctionResult>[]>(
    _transactions: TransactionItems,
    results: results,
    positions: number[][],
  ): TransactionItems {
    const transactions = structuredClone(_transactions)

    for (let i = 0; i < results.length; i++) {
      const result = results[i]
      const position = positions[i]
      let target = transactions

      // @ts-ignore - position might be undefined
      for (let j = 0; j < position.length - 1; j++) {
        // @ts-ignore - position might be undefined
        const transaction = target[position[j]]
        if ('transactions' in transaction && transaction.transactions) {
          target = transaction.transactions
        }
      }

      // @ts-ignore - position might be undefined
      const transaction = target[position[position.length - 1]]

      if (!isCompoundTransaction(transaction)) {
        // @ts-ignore - position might be undefined
        target[position[position.length - 1]] = {
          ...transaction,
          result,
        }
      }
    }

    return transactions
  }

  // @ts-ignore - Override type mismatch
  typeGuardConvertSingle(transaction: TransactionItemUnion): ContractFunctionConfig | ContractFunctionConfig[] {
    if (isSwapTransaction(transaction)) {
      return this.swapTransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (isVaultTransaction(transaction)) {
      // VaultTransactionFactory can return an array (e.g., SkimIn produces transferFrom + skim)
      return this.vaultTransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (isPermitTransactionItem(transaction)) {
      return this.permit2TransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (isEVCTransactionItem(transaction)) {
      return this.evcTransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (
      isRewardStreamsTransaction(transaction) ||
      isMerklTransaction(transaction) ||
      isUnlockREulTransaction(transaction)
    ) {
      return this.rewardStreamsTransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (isSwapVerifierTransactionItem(transaction)) {
      return this.swapVerifierTransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (isTosTransactionItem(transaction)) {
      return this.tosTransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (isFeeFlowTransactionItem(transaction)) {
      return this.feeFlowTransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (isEulerSwapTransactionItem(transaction)) {
      return this.eulerSwapTransactionFactory.typeGuardConvertSingle(transaction)
    }

    if (isKeyringTransactionItem(transaction)) {
      return this.keyringTransactionFactory.typeGuardConvertSingle(transaction)
    }

    throw this.errorUnsupportedTransactionType(transaction)
  }
}
